// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { ApiServiceError } from "../../services/api/apiError";
import { apiService } from "../../services/api/apiService";
import {
  LOCALE_PREFERENCE_KEY,
  parseAppLocale,
  type AppLocale,
} from "../../i18n/appLocale";
import { shouldUseDemoData } from "../../services/api/demoData";

export interface LocaleApiContext {
  token?: string;
  allowTestModeRequests?: boolean;
}

interface LocalePreferenceDto {
  value?: string;
}

interface ApiEnvelope<T> {
  data?: T;
  error?: { message?: string };
}

function tokenOption(
  context: LocaleApiContext,
): { token: string } | Record<string, never> {
  return context.token !== undefined ? { token: context.token } : {};
}

function unwrap<T>(envelope: ApiEnvelope<T>): T {
  if (envelope.error?.message !== undefined) {
    throw new Error(envelope.error.message);
  }
  if (envelope.data === undefined) {
    throw new Error("API response was missing the expected data payload.");
  }
  return envelope.data;
}

export async function readLocalePreference(
  context: LocaleApiContext,
): Promise<AppLocale | null> {
  if (shouldUseDemoData(context)) {
    return null;
  }
  try {
    const response = await apiService.requestJson<ApiEnvelope<LocalePreferenceDto>>({
      path: `/api/me/preferences/${LOCALE_PREFERENCE_KEY}`,
      ...tokenOption(context),
    });
    const value = unwrap(response.unwrap()).value;
    if (value !== "en" && value !== "es") {
      return null;
    }
    return parseAppLocale(value);
  } catch (error: unknown) {
    if (error instanceof ApiServiceError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function writeLocalePreference(
  context: LocaleApiContext,
  locale: AppLocale,
): Promise<void> {
  if (shouldUseDemoData(context)) {
    return;
  }
  const response = await apiService.requestJson<
    ApiEnvelope<LocalePreferenceDto>,
    { key: string; value: string }
  >({
    path: "/api/me/preferences",
    method: "PUT",
    ...tokenOption(context),
    body: { key: LOCALE_PREFERENCE_KEY, value: locale },
  });
  unwrap(response.unwrap());
}
